/**
 * Phrase de recuperation d'un portefeuille crypto (entree « portefeuille crypto »).
 *
 * Une faute de frappe dans une phrase recopiee ne se voit qu'au jour de la restauration -- trop tard : les fonds sont
 * perdus. Ce module reconnait les formes standard et refuse ce qui est surement faux, SANS jamais refuser une phrase
 * juste d'un autre format :
 *   - BIP39 anglais : 12/15/18/21/24 mots de la liste, somme de controle SHA-256 (BIP-0039) ;
 *   - Electrum 2.0+ : version de graine `01`/`100`/`101`/`102` par HMAC-SHA512 (cle « Seed version »), texte normalise
 *     comme Electrum le fait (`normalize_text`) -- ses graines ne sont, par construction, pas du BIP39 valide ;
 *   - Electrum ancienne : 12 ou 24 mots de l'ancienne liste (aucune somme de controle a verifier).
 * Le reste (autre langue, Monero, 25 mots…) n'est pas juge. Pur, synchrone, aucune E/S ; primitives `@noble/hashes`.
 */
import { hmac } from '@noble/hashes/hmac.js';
import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { BIP39_ENGLISH, ELECTRUM_OLD_WORDS } from './seed-wordlists.js';

const BIP39_INDEX: ReadonlyMap<string, number> = new Map(BIP39_ENGLISH.map((word, index) => [word, index]));
const ELECTRUM_OLD: ReadonlySet<string> = new Set(ELECTRUM_OLD_WORDS);
/** BIP39 : 128 a 256 bits d'entropie, par pas de 32 -> 12 a 24 mots, par pas de 3. */
const BIP39_WORD_COUNTS: ReadonlySet<number> = new Set([12, 15, 18, 21, 24]);
/** Prefixes de version des graines Electrum : standard, segwit, 2FA, 2FA segwit (`electrum/version.py`). */
const ELECTRUM_SEED_PREFIXES = ['01', '100', '101', '102'] as const;
/** Au-dela, une phrase n'est plus « BIP39 avec une faute » mais autre chose (autre langue, autre format). */
const MAX_UNKNOWN_WORDS = 2;

export type RecoveryPhraseKind = 'bip39' | 'electrum' | 'electrum-old' | 'checksum' | 'unknown-words' | 'unchecked';

export interface RecoveryPhraseCheck {
  readonly kind: RecoveryPhraseKind;
  readonly words: number;
  /** `unknown-words` : les mots absents de la liste BIP39, avec le mot de la liste qui partage leurs 4 premieres lettres. */
  readonly unknown: readonly { readonly word: string; readonly suggestion: string | null }[];
}

/** Blocs d'ecriture sans espace entre les mots (idéogrammes, kana, hangul…) : `CJK_INTERVALS` d'Electrum. */
const CJK_INTERVALS: readonly (readonly [number, number])[] = [
  [0x4e00, 0x9fff], [0x3400, 0x4dbf], [0x20000, 0x2a6df], [0x2a700, 0x2b73f], [0x2b740, 0x2b81f], [0xf900, 0xfaff],
  [0x2f800, 0x2fa1d], [0x3190, 0x319f], [0x2e80, 0x2eff], [0x2f00, 0x2fdf], [0x31c0, 0x31ef], [0x2ff0, 0x2fff],
  [0xe0100, 0xe01ef], [0x3100, 0x312f], [0x31a0, 0x31bf], [0xff00, 0xffef], [0x3040, 0x309f], [0x30a0, 0x30ff],
  [0x31f0, 0x31ff], [0x1b000, 0x1b0ff], [0xac00, 0xd7af], [0x1100, 0x11ff], [0xa960, 0xa97f], [0xd7b0, 0xd7ff],
  [0x3130, 0x318f], [0xa4d0, 0xa4ff], [0x16f00, 0x16f9f], [0xa000, 0xa48f], [0xa490, 0xa4cf],
];

function isCjk(char: string | undefined): boolean {
  const code = char?.codePointAt(0);
  return code !== undefined && CJK_INTERVALS.some(([min, max]) => code >= min && code <= max);
}

/**
 * Texte d'une graine tel qu'Electrum le hache (`normalize_text`) : NFKD, minuscules, accents retires, espaces reduits a
 * un seul, et aucun espace entre deux caracteres CJK.
 */
export function normalizeSeedText(phrase: string): string {
  const chars = [...phrase.normalize('NFKD').toLowerCase().replace(/\p{M}/gu, '').trim().split(/\s+/u).join(' ')];
  return chars.filter((char, i) => !(char === ' ' && isCjk(chars[i - 1]) && isCjk(chars[i + 1]))).join('');
}

/** Phrase rangee dans le coffre : espaces de bord retires, un seul espace entre les mots (la casse est gardee). */
export function normalizeRecoveryPhrase(phrase: string): string {
  return phrase.trim().split(/\s+/u).filter(Boolean).join(' ');
}

function isBip39ChecksumValid(indices: readonly number[]): boolean {
  const checksumBits = indices.length / 3;
  const entropyBits = indices.length * 11 - checksumBits;
  const bits = indices.map((index) => index.toString(2).padStart(11, '0')).join('');
  const entropy = new Uint8Array(entropyBits / 8);
  for (let i = 0; i < entropy.length; i += 1) entropy[i] = Number.parseInt(bits.slice(i * 8, i * 8 + 8), 2);
  const hash = sha256(entropy);
  const expected = [...hash.slice(0, 1)].map((byte) => byte.toString(2).padStart(8, '0')).join('').slice(0, checksumBits);
  return bits.slice(entropyBits) === expected;
}

function isElectrumSeed(normalized: string): boolean {
  const digest = hmac(sha512, new TextEncoder().encode('Seed version'), new TextEncoder().encode(normalized));
  const hex = [...digest.slice(0, 2)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return ELECTRUM_SEED_PREFIXES.some((prefix) => hex.startsWith(prefix));
}

/** Mot BIP39 qui partage les 4 premieres lettres (elles suffisent a designer un mot de la liste, BIP-0039). */
function bip39Suggestion(word: string): string | null {
  if (word.length < 4) return null;
  const prefix = word.slice(0, 4);
  return BIP39_ENGLISH.find((candidate) => candidate.startsWith(prefix)) ?? null;
}

/** Reconnait la phrase ; `checksum` et `unknown-words` designent une phrase BIP39 surement mal recopiee. */
export function checkRecoveryPhrase(phrase: string): RecoveryPhraseCheck {
  const normalized = normalizeSeedText(phrase);
  const words = normalized.length === 0 ? [] : normalized.split(' ');
  const result = (kind: RecoveryPhraseKind, unknown: RecoveryPhraseCheck['unknown'] = []): RecoveryPhraseCheck => ({ kind, words: words.length, unknown });
  if (words.length === 0) return result('unchecked');
  const indices = words.map((word) => BIP39_INDEX.get(word));
  const known = indices.filter((index): index is number => index !== undefined);
  if (BIP39_WORD_COUNTS.has(words.length) && known.length === words.length && isBip39ChecksumValid(known)) return result('bip39');
  if (isElectrumSeed(normalized)) return result('electrum');
  if ((words.length === 12 || words.length === 24) && words.every((word) => ELECTRUM_OLD.has(word))) return result('electrum-old');
  if (!BIP39_WORD_COUNTS.has(words.length)) return result('unchecked');
  if (known.length === words.length) return result('checksum');
  const unknown = words.filter((word) => !BIP39_INDEX.has(word));
  if (unknown.length <= MAX_UNKNOWN_WORDS) return result('unknown-words', unknown.map((word) => ({ word, suggestion: bip39Suggestion(word) })));
  return result('unchecked');
}

/**
 * Ce qui empeche d'enregistrer la phrase : une phrase BIP39 dont un mot n'est pas de la liste ou dont la somme de
 * controle ne tient pas. Retourne la phrase a afficher, `null` si rien n'est renseigne ou si rien n'est surement faux.
 */
export function recoveryPhraseProblem(phrase: string): string | null {
  const check = checkRecoveryPhrase(phrase);
  if (check.kind === 'checksum') {
    return 'Phrase de recuperation invalide : tous les mots sont dans la liste BIP39 mais la somme de controle ne tient pas (un mot faux ou deux mots inverses).';
  }
  if (check.kind === 'unknown-words') {
    const words = check.unknown.map(({ word, suggestion }) => `« ${word} »${suggestion !== null ? ` (« ${suggestion} » ?)` : ''}`).join(', ');
    return `Phrase de recuperation invalide : ${words} ${check.unknown.length > 1 ? 'ne sont pas des mots' : 'n’est pas un mot'} de la liste BIP39.`;
  }
  return null;
}

/** Ce que la fiche dit de la phrase : format reconnu, ou rien si elle n'a pas ete jugee. */
export function recoveryPhraseLabel(check: RecoveryPhraseCheck): string | null {
  if (check.kind === 'bip39') return `BIP39 · ${check.words} mots`;
  if (check.kind === 'electrum') return `Electrum · ${check.words} mots`;
  if (check.kind === 'electrum-old') return `Electrum (ancienne) · ${check.words} mots`;
  return null;
}
